import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { PageSubHeader } from '@/components/organisms/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { useAudiencePage, useErrorChipsFilter } from '@/modules/campaigns/hooks/useAudiencePage';
import { useCampaigns } from '@/modules/campaigns/hooks/useCampaigns';
import {
  selectCsvHeaders,
  selectCsvLiveInstructionTargets,
  selectCsvMappings,
  selectCsvRows,
} from '@/modules/campaigns/redux/selectors';
import { TABLE_LAYOUT_FILL_FULL_WIDTH_CLASSES } from '@/util/constants';

import { useUnmatchedFields } from './hooks/useUnmatchedFields';
import {
  AudienceErrorChipsBar,
  AudienceIssueRow,
  AudienceSectionHeader,
  ReviewAudienceTable,
} from '../../components/AudienceReview';
import { AudienceToolbar } from '../../components/AudienceToolbar';
import { CampaignBreadcrumbs } from '../../components/CampaignBreadcrumbs';
import { MatchFieldsModal } from '../../components/modals/MatchFieldsModal';
import { getDebtCampaignVariables } from '../../constants';
import {
  setCsvLiveInstructionTargets,
  setCsvMappingData,
  setDraftAudienceStats,
  setDraftSubscribers,
} from '../../redux/actions';
import { ColumnMapping } from '../../types/column-mapping';
import {
  computeAudienceStats,
  getExtraRequiredFields,
  isRequiredUnmatchedVariable,
  transformCsvWithMappings,
  validateSubscriberFields,
} from '../../utils';

export const PaymentRecoveryAudience = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isMatchFieldsOpen, setIsMatchFieldsOpen] = useState(false);

  const page = useAudiencePage({ ensureRequiredColumns: true });
  const { campaign } = useCampaigns(page.campaignId);

  // CSV data for inline re-mapping
  const csvHeaders = useSelector(selectCsvHeaders);
  const csvMappings = useSelector(selectCsvMappings);
  const csvRows = useSelector(selectCsvRows);
  const csvLiveInstructionTargets = useSelector(selectCsvLiveInstructionTargets);

  // Stable references for the modal props so its internal memos don't recompute
  // on every parent render.
  const matchFieldsHeaders = useMemo(() => csvHeaders ?? [], [csvHeaders]);
  const matchFieldsSampleRows = useMemo(() => csvRows?.slice(0, 3) ?? [], [csvRows]);

  const availableVariables = useMemo(() => getDebtCampaignVariables(), []);

  const {
    errorCount,
    errorNames,
    errorFilterActive,
    visibleChips,
    tableData,
    tableRowCount,
    showErrors,
    removeChip,
  } = useErrorChipsFilter(page);

  // Batch re-mapping handler (from MatchFieldsModal)
  const handleMatchFieldsConfirm = useCallback(
    (newMappings: Record<string, ColumnMapping>, liveInstructionTargets: string[]) => {
      if (!csvHeaders || !csvRows) {
        return;
      }
      const raw = transformCsvWithMappings(
        csvHeaders,
        csvRows,
        newMappings,
        liveInstructionTargets
      );
      // No contact column → zero rows; toast and keep the previous mapping instead of wiping.
      if (raw.length === 0) {
        toast.error(t('file_upload.csv.missing_required_columns'));
        return;
      }
      dispatch(setCsvMappingData({ headers: csvHeaders, mappings: newMappings, rows: csvRows }));
      dispatch(setCsvLiveInstructionTargets(liveInstructionTargets));
      const transformed = validateSubscriberFields(raw, t);
      dispatch(setDraftSubscribers({ subscribers: transformed }));
      dispatch(setDraftAudienceStats(computeAudienceStats(transformed, csvHeaders.length)));
    },
    [csvHeaders, csvRows, dispatch, t]
  );

  const extraRequiredFields = useMemo(
    () => getExtraRequiredFields(page.displayData),
    [page.displayData]
  );

  const { unmatchedFields } = useUnmatchedFields({
    csvMappings: csvMappings ?? null,
    isShowingReduxData: page.isShowingReduxData,
    extraRequiredFields,
  });

  // An unmatched contact-group member (email/phone) blocks like a required field.
  const requiredUnmatched = unmatchedFields.filter((f) => isRequiredUnmatchedVariable(f));
  const optionalUnmatched = unmatchedFields.filter((f) => !isRequiredUnmatchedVariable(f));
  const warningsCount = (errorCount > 0 ? 1 : 0) + (optionalUnmatched.length > 0 ? 1 : 0);

  const openMatchFields = () => setIsMatchFieldsOpen(true);

  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <CampaignBreadcrumbs
            campaignName={page.campaignName || campaign?.name}
            currentStep={t('campaigns.audience')}
          />
        }
      />

      <PageContentWrapper noGutters>
        <div className={TABLE_LAYOUT_FILL_FULL_WIDTH_CLASSES}>
          {requiredUnmatched.length > 0 && (
            <div className="mb-4 shrink-0">
              <AudienceSectionHeader
                variant="required"
                title={t('campaigns.audience_warnings.required_actions')}
                count={1}
              />
              <AudienceIssueRow
                title={t('campaigns.audience_warnings.unmatched_required_fields', {
                  count: requiredUnmatched.length,
                })}
                detail={requiredUnmatched.map((f) => f.label).join(', ')}
                actionLabel={t('campaigns.audience_warnings.match_fields_link')}
                onAction={openMatchFields}
              />
            </div>
          )}

          {warningsCount > 0 && (
            <div className="mb-4 shrink-0">
              <AudienceSectionHeader
                variant="warning"
                title={t('campaigns.audience_warnings.title')}
                count={warningsCount}
              />
              {errorCount > 0 && (
                <AudienceIssueRow
                  title={t('campaigns.audience_warnings.error_count', { count: errorCount })}
                  detail={errorNames}
                  actionLabel={t('campaigns.audience_warnings.view')}
                  onAction={showErrors}
                />
              )}
              {optionalUnmatched.length > 0 && (
                <AudienceIssueRow
                  title={t('campaigns.audience_warnings.unmatched_optional_fields', {
                    count: optionalUnmatched.length,
                  })}
                  detail={optionalUnmatched.map((f) => f.label).join(', ')}
                  actionLabel={t('campaigns.audience_warnings.match_fields_link')}
                  onAction={openMatchFields}
                />
              )}
            </div>
          )}

          <AudienceToolbar page={page} />

          {errorFilterActive && (
            <AudienceErrorChipsBar chips={visibleChips} onRemove={removeChip} />
          )}

          <ReviewAudienceTable page={page} data={tableData} rowCount={tableRowCount} />
        </div>

        <MatchFieldsModal
          open={isMatchFieldsOpen}
          onOpenChange={setIsMatchFieldsOpen}
          csvHeaders={matchFieldsHeaders}
          sampleRows={matchFieldsSampleRows}
          availableVariables={availableVariables}
          initialMappings={csvMappings}
          initialLiveInstructionTargets={csvLiveInstructionTargets}
          skipAutoSuggest
          extraRequiredFields={extraRequiredFields}
          onConfirm={handleMatchFieldsConfirm}
        />
      </PageContentWrapper>
    </>
  );
};
